/**
 * Gộp toàn bộ config theo placement thành một object duy nhất — Prompt.md §14.
 *
 * Vì sao cần lớp này: engine chỉ nhận `AppConfig` đã resolve, KHÔNG tự biết `CameraPlacement`
 * để đi tra `defaultPostureConfig[placement]`. Nhờ đó classifier/state machine không có bất kỳ
 * nhánh `if (placement === "SIDE")` nào (quyết định BLOCKER #1 Option C) — mode chỉ tồn tại ở
 * ranh giới resolve config và ở bảng ISSUE_AVAILABILITY.
 *
 * Kèm theo là chỗ duy nhất để tuning ở Phase 12 (Settings > Developer): truyền `overrides` mà
 * không phải build lại app.
 */

import type { CameraPlacement } from "@studystraight/shared-types";

import { defaultAlertConfig, type AlertConfig } from "./alertConfig.js";
import { defaultCalibrationConfig, type CalibrationConfig } from "./calibrationConfig.js";
import { defaultPostureConfig, type PostureConfig } from "./postureThresholds.js";
import { defaultScoreWeights, type ScoreWeights } from "./scoreWeights.js";
import { defaultSessionConfig, type SessionConfig } from "./sessionConfig.js";
import { defaultTemporalConfig, type TemporalConfig } from "./temporalConfig.js";
import { defaultVisionConfig, type VisionConfig } from "./visionConfig.js";

export interface AppConfig {
  placement: CameraPlacement;
  posture: PostureConfig;
  weights: ScoreWeights;
  temporal: TemporalConfig;
  alert: AlertConfig;
  calibration: CalibrationConfig;
  vision: VisionConfig;
  session: SessionConfig;
}

/**
 * Chỉ đi sâu vào object thuần. Array được thay nguyên khối (xem `mergeDeep`), nên giữ nguyên
 * type của nó thay vì biến thành `DeepPartial<T[number]>[]` — override một phần
 * `severityPriority` là vô nghĩa.
 */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends readonly unknown[]
    ? T[K]
    : T[K] extends object
      ? DeepPartial<T[K]>
      : T[K];
};

export type AppConfigOverrides = DeepPartial<Omit<AppConfig, "placement">>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Deep merge KHÔNG mutate `base`.
 *
 * Array được thay nguyên khối, không merge theo index: `severityPriority` là một thứ tự ưu tiên,
 * merge từng phần tử sẽ tạo ra danh sách nửa cũ nửa mới có thể thiếu hoặc trùng issue.
 */
function mergeDeep<T>(base: T, override: unknown): T {
  if (!isPlainObject(override)) {
    return override === undefined ? base : (override as T);
  }
  if (!isPlainObject(base)) {
    return override as T;
  }

  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(override)) {
    const patch = override[key];
    if (patch === undefined) continue;
    out[key] = isPlainObject(patch) ? mergeDeep(out[key], patch) : patch;
  }
  return out as T;
}

/**
 * Chọn bộ config cho `placement` rồi áp `overrides` lên trên.
 *
 * Không bao giờ ghi vào `defaultPostureConfig` và các default khác: chúng là module-level
 * singleton, một lần bị mutate là mọi phiên học sau đó lệch theo. Nhánh nào của cây config bị
 * override sẽ được clone; nhánh không bị override vẫn dùng chung reference với default, nên
 * config trả về phải được coi là read-only (mọi field đều là primitive nên không có nhu cầu ghi).
 */
export function resolveConfig(
  placement: CameraPlacement,
  overrides?: AppConfigOverrides,
): AppConfig {
  const base: AppConfig = {
    placement,
    posture: defaultPostureConfig[placement],
    weights: defaultScoreWeights[placement],
    temporal: defaultTemporalConfig,
    alert: defaultAlertConfig,
    calibration: defaultCalibrationConfig,
    vision: defaultVisionConfig,
    session: defaultSessionConfig,
  };

  return overrides ? mergeDeep(base, { ...overrides, placement }) : mergeDeep(base, {});
}
